import React, { useMemo } from 'react';
import { AlertCircle, Clock, FileText, MapPin } from 'lucide-react';
// Updated Interface
export interface EmergencyIntervention {
  id: number;
  lat: number;
  lng: number;
  address: string;
  zoneName: string;
  cleaningType: string;
  description: string;
  mq: number;
  rooms: number;
  bathrooms: number;
  floor: number;
  tags: string[];
  hostName: string;
  nextCheckIn: string;
  deadlineMs: number;
  quotesReceived: number;
  suggestedPriceMin: number;
  suggestedPriceMax: number;
}
interface EmergencyHoverTooltipProps {
  intervention: EmergencyIntervention;
  position: {
    x: number;
    y: number;
  };
  visible: boolean;
}
export function EmergencyHoverTooltip({
  intervention,
  position,
  visible
}: EmergencyHoverTooltipProps) {
  // Calculate time remaining
  const timeRemaining = useMemo(() => {
    const now = Date.now();
    const diff = intervention.deadlineMs - now;
    if (diff <= 0) return 'Scaduto';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }, [intervention.deadlineMs]);
  if (!visible) return null;
  return <div className="absolute z-[1000] pointer-events-none transition-opacity duration-200" style={{
    left: position.x + 20,
    top: position.y,
    transform: 'translateY(-50%)',
    opacity: visible ? 1 : 0
  }} data-id="element-1339">
      <div className="bg-white rounded-xl shadow-2xl border border-red-100 p-4 w-[280px] relative" data-id="element-1340">
        {/* Arrow pointing left */}
        <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-white border-l border-b border-red-100 transform rotate-45" style={{
        boxShadow: '-2px 2px 2px -2px rgba(0,0,0,0.05)'
      }} data-id="element-1341"></div>

        {/* Header */}
        <div className="relative z-10" data-id="element-1342">
          <div className="flex justify-between items-start mb-2" data-id="element-1343">
            <div data-id="element-1344">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-1345">
                <AlertCircle size={14} className="text-red-600" data-id="element-1346" />
                <h3 className="font-bold text-navy text-sm" data-id="element-1347">
                  Intervento Urgente
                </h3>
              </div>
              <p className="text-xs text-gray-500 font-medium" data-id="element-1348">
                {intervention.cleaningType}
              </p>
            </div>
          </div>

          <div className="border-t border-gray-100 my-3" data-id="element-1349"></div>

          {/* Details */}
          <div className="space-y-3" data-id="element-1350">
            {/* Countdown */}
            <div className="flex items-start gap-3" data-id="element-1351">
              <div className="bg-red-50 rounded-lg p-1.5 flex-shrink-0" data-id="element-1352">
                <Clock size={14} className="text-red-600" data-id="element-1353" />
              </div>
              <div data-id="element-1354">
                <p className="text-xs font-medium text-navy" data-id="element-1355">
                  Chiusura offerte
                </p>
                <p className="text-xs text-red-600 font-bold" data-id="element-1356">
                  tra {timeRemaining}
                </p>
              </div>
            </div>

            {/* Quotes Count */}
            <div className="flex items-start gap-3" data-id="element-1357">
              <div className="bg-gray-50 rounded-lg p-1.5 flex-shrink-0" data-id="element-1358">
                <FileText size={14} className="text-gray-500" data-id="element-1359" />
              </div>
              <div data-id="element-1360">
                <p className="text-xs font-medium text-navy" data-id="element-1361">Competizione</p>
                <p className="text-xs text-gray-600" data-id="element-1362">
                  {intervention.quotesReceived} preventivi ricevuti
                </p>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3" data-id="element-1363">
              <div className="bg-gray-50 rounded-lg p-1.5 flex-shrink-0" data-id="element-1364">
                <MapPin size={14} className="text-gray-500" data-id="element-1365" />
              </div>
              <div data-id="element-1366">
                <p className="text-xs font-medium text-navy" data-id="element-1367">Luogo</p>
                <p className="text-xs text-gray-600 truncate max-w-[180px]" data-id="element-1368">
                  {intervention.address}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}